import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CommonPopover } from '../../../../../../global/common-popover/common-popover';
import { CommonPopoverContext } from '../../../../../../global/common-popover/common-popover.context';
import { SearchInput } from '../../../../../../global/search-input/search-input';
import { Component } from '../../../../../../../infrastructure/type-extensions';
import { DefaultClosePopoverButton } from '../../../../../../global/common-popover/default-close-popover-button';
import { getFilteredAttendees } from '../../../../../../../infrastructure/utils/attendee.utils';
import { OrganizationRole } from '../../../../../../../modules/connections/types';
import ParticipantsList from './participants-list';
import { IFilteredOrganization } from '../../../../../../../api/attendees.dao';
import { cancelText } from '../../../../../../../constants/common-labels.constants';

export interface IEntityData {
    attendeeEntityId: number;
    orgEntityId: number;
    orgRole: OrganizationRole;
}

interface IFormContainerDerivedProps {
    isOpen: boolean;
}

const makeAssignToParticipantFormInPopover = <T extends IFormContainerDerivedProps>(PopoverTriggerComponent: Component<T>) => {
    // we need to utilize context.consumer in order to identify 'isOpen' value, but usage of popover forcing ref forwarding
    // therefore utilize React.forwardRef to create HOC over the PopoverTriggerComponent, which would utilize context and forward ref
    const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>) => {
        return (
            <CommonPopoverContext.Consumer>
                {context => <PopoverTriggerComponent {...props} isOpen={context.isOpen} ref={ref} />}
            </CommonPopoverContext.Consumer>
        );
    });
    // T can not be casted to ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>
    // can't get the reason for this, but for now just cast targetComponentProps to any while passing to component with forwarded ref
    // tslint:disable:no-any
    return props => {
        const {
            orgRole,
            itemsList,
            onChange,
            meetingConnectionId,
        } = props;

        const [filterCriteria, setFilterCriteria] = useState<string>('');
        const [selectedItem, setSelectedItem] = useState<number | null>(null);
        const [entityData, setEntityData] = useState<IEntityData>(null);

        let filteredItems: IFilteredOrganization[] = itemsList;

        const clearData = () => {
            setSelectedItem(null);
            setEntityData(null);
            setFilterCriteria('');
        };

        if (itemsList) {
            if (!filterCriteria) {
                filteredItems = itemsList;
            } else {
                filteredItems = getFilteredAttendees(itemsList, filterCriteria);
            }
        }

        const shallRenderNoItems = !itemsList.length;
        const shallRenderEmptySearch = !shallRenderNoItems && !filteredItems.length;
        const shallRenderList = !shallRenderNoItems && !shallRenderEmptySearch;

        useEffect(() => {
            setSelectedItem(null);
        }, [filterCriteria]);

        return (
            <CommonPopover
                className='oe-popup-layout__body--fixed-width'
                header={
                    <SearchInput
                        placeholder={'Search'}
                        onChange={e => setFilterCriteria(e.target.value)}
                        className='w-100'
                        value={filterCriteria}
                        autoFocus={true}
                    />
                }
                body={(
                    <>
                        { shallRenderNoItems && <div className='single-select-no-content'>
                            <FormattedMessage
                            id={`oe.items.no${orgRole}InList`}
                            description='Label text'
                            defaultMessage={`No ${orgRole} in the list`}
                        /></div> }
                        { shallRenderEmptySearch && <div className='single-select-no-content'>
                            <FormattedMessage
                            id='oe.items.noMatchingSearchCriteria'
                            description='Label text'
                            defaultMessage='No items matching search criteria'
                        /></div>}
                        { shallRenderList &&
                            <ParticipantsList
                                itemsList={filteredItems}
                                onSelectItem={setSelectedItem}
                                selectedItem={selectedItem}
                                onSetEntity={setEntityData}
                            />
                        }
                    </>
                )}
                footer={
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultClosePopoverButton
                            text={<FormattedMessage description='Button text' defaultMessage='Assign' id='oe.common.assign' />}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => {
                                if (!!selectedItem) {
                                    onChange(
                                        entityData.attendeeEntityId,
                                        entityData.orgEntityId,
                                        meetingConnectionId,
                                        entityData.orgRole
                                    );
                                }
                            }}
                        />
                        <DefaultClosePopoverButton
                            text={cancelText()}
                        />
                    </div>
                }
                position='left'
                align='start'
                onClose={clearData}
            >
                <ComponentWithForwardedRef { ...props } />
            </CommonPopover>
            // tslint:enable:no-any
        );
    };
};


export { makeAssignToParticipantFormInPopover, IFormContainerDerivedProps };
